import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { 
  Lightbulb, 
  CheckCircle2,
  AlertTriangle,
  TrendingUp,
  Target,
  Loader2,
  Calendar,
  Sparkles,
  RefreshCw
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ActionPlan = () => {
  const { organization } = useAuth();
  const [actionPlan, setActionPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [noReviews, setNoReviews] = useState(false);

  useEffect(() => {
    if (organization) {
      fetchActionPlan();
    } else {
      setLoading(false);
    }
  }, [organization]);

  const fetchActionPlan = async () => {
    try {
      const response = await axios.get(`${API}/action-plan`);
      setActionPlan(response.data);
      setNoReviews(false);
    } catch (error) {
      console.error("Error fetching action plan:", error);
      if (error.response?.status === 404 && error.response?.data?.detail === "no_reviews") {
        setNoReviews(true);
      } else {
        toast.error("Erreur lors du chargement du plan d'action");
      }
    } finally {
      setLoading(false);
    }
  };

  const regenerateActionPlan = async () => {
    setRegenerating(true);
    try {
      const response = await axios.post(`${API}/action-plan/regenerate`);
      setActionPlan(response.data);
      toast.success("Plan d'action régénéré avec succès !");
    } catch (error) {
      console.error("Error regenerating action plan:", error);
      toast.error("Erreur lors de la régénération du plan d'action");
    } finally {
      setRegenerating(false);
    }
  };

  // No organization screen (user not onboarded)
  if (!organization) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="action-plan-page-no-organization">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">
            Plan d'action IA
          </h1>
          <p className="text-slate-500 mt-1">
            Recevez des recommandations personnalisées chaque mois
          </p>
        </div>

        <div className="bg-gradient-to-br from-violet-50 to-pink-50 rounded-2xl p-8 border border-violet-100 text-center">
          <Target className="w-12 h-12 text-violet-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-slate-900 mb-2">
            Configurez votre établissement
          </h3>
          <p className="text-slate-600 mb-6 max-w-md mx-auto">
            Complétez l'onboarding pour recevoir votre plan d'action personnalisé.
          </p>
          <Link to="/onboarding">
            <Button className="rounded-full bg-gradient-to-r from-violet-600 to-pink-500 hover:opacity-90" data-testid="complete-onboarding">
              Compléter l'onboarding
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  // No reviews yet - show helpful message
  if (noReviews) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="action-plan-no-reviews">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">
            Plan d'action IA
          </h1>
          <p className="text-slate-500 mt-1">
            Recevez des recommandations personnalisées chaque mois
          </p>
        </div>

        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 border border-amber-100 text-center">
          <Sparkles className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-slate-900 mb-2">
            Pas encore d'avis à analyser
          </h3>
          <p className="text-slate-600 mb-6 max-w-md mx-auto">
            L'IA génèrera votre plan d'action personnalisé dès que vous aurez collecté vos premiers avis clients via votre QR code.
          </p>
          <Link to="/qrcode">
            <Button className="rounded-full bg-gradient-to-r from-amber-500 to-orange-500 hover:opacity-90" data-testid="view-qrcode">
              Voir mon QR code
            </Button>
          </Link>
        </div>

        <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
          <div className="flex items-start gap-4">
            <Lightbulb className="w-6 h-6 text-amber-500 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-slate-900 mb-1">Comment ça fonctionne ?</h4>
              <p className="text-sm text-slate-600">
                Partagez votre QR code avec vos clients. Une fois les premiers avis collectés, 
                l'IA analysera automatiquement les retours (en priorisant les avis négatifs) 
                et générera des recommandations concrètes pour améliorer votre établissement.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="action-plan-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">
              Plan d'action IA
            </h1>
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-violet-500 to-pink-500 text-white rounded-full text-xs font-medium">
              <Sparkles className="w-3 h-3" /> IA
            </span>
          </div>
          <p className="text-slate-500">
            Recommandations personnalisées pour {actionPlan?.month}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {actionPlan?.generated_at && (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Calendar className="w-4 h-4" />
              <span>Généré le {formatDate(actionPlan.generated_at)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="bg-gradient-to-br from-violet-50 to-pink-50 rounded-2xl p-6 border border-violet-100">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
            <Sparkles className="w-6 h-6 text-violet-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 mb-2">Synthèse mensuelle</h3>
            <p className="text-slate-700 leading-relaxed">
              {actionPlan?.summary || "Analyse en cours..."}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Strengths */}
        <div className="bg-white rounded-2xl p-6 border border-slate-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            </div>
            <h3 className="font-semibold text-slate-900">Points forts</h3>
          </div>
          <ul className="space-y-3">
            {actionPlan?.strengths?.map((strength, index) => (
              <li key={index} className="flex items-start gap-3">
                <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-medium text-emerald-700">{index + 1}</span>
                </div>
                <p className="text-slate-600">{strength}</p>
              </li>
            ))}
          </ul>
        </div>

        {/* Improvements */}
        <div className="bg-white rounded-2xl p-6 border border-slate-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
            </div>
            <h3 className="font-semibold text-slate-900">Axes d'amélioration</h3>
          </div>
          <ul className="space-y-3">
            {actionPlan?.improvements?.map((improvement, index) => (
              <li key={index} className="flex items-start gap-3">
                <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-medium text-orange-700">{index + 1}</span>
                </div>
                <p className="text-slate-600">{improvement}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Recommendations */}
      <div className="bg-white rounded-2xl p-6 border border-slate-100">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-violet-50 rounded-xl flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-violet-600" />
          </div>
          <h3 className="font-semibold text-slate-900">Recommandations concrètes</h3>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {actionPlan?.recommendations?.map((recommendation, index) => (
            <div 
              key={index} 
              className="bg-gradient-to-br from-slate-50 to-violet-50/50 rounded-xl p-4 border border-slate-100"
            >
              <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center mb-3">
                <span className="font-bold text-violet-700">{index + 1}</span>
              </div>
              <p className="text-slate-700 text-sm leading-relaxed">{recommendation}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
        <div className="flex items-start gap-4">
          <Lightbulb className="w-6 h-6 text-amber-500 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-slate-900 mb-1">Comment utiliser ce plan ?</h4>
            <p className="text-sm text-slate-600">
              Ce plan d'action est généré automatiquement par l'IA en analysant vos avis du mois, 
              en priorisant les commentaires négatifs (moins de 4 étoiles). 
              Utilisez-le comme guide pour prioriser vos actions d'amélioration. 
              Un nouveau plan sera disponible le mois prochain.
            </p>
          </div>
        </div>
      </div>

      {/* Link to KPIs */}
      <div className="bg-white rounded-2xl p-6 border border-slate-100">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="font-semibold text-slate-900 mb-1">Suivez vos KPIs</h3>
            <p className="text-sm text-slate-500">
              Visualisez l'évolution de votre e-réputation et l'impact de vos actions.
            </p>
          </div>
          <Link to="/kpis">
            <Button className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="view-kpis">
              Voir les KPIs
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ActionPlan;
